'use client';

import { useCallback, useEffect, useState } from 'react';
import { ArticleCardGrid } from '@/app_components/ArticleSections/ArticleCards/ArticleCardGrid';
import { StandardHeading } from '@/app_components/ArticleSections/Heading';
import { NativoCarouselFeedSlot } from '@/app_components/ArticleSections/NativoArticleFeedSlots';
import { Carousel } from '@/app_components/ui/Carousel';
import type { ArticlePreviewGrid } from '@/app_lib/types/ArticlePreview';
import { cn } from '@/app_lib/utils/utils';

interface ArticleSectionCarouselProps {
  articles: ArticlePreviewGrid[];
  className?: string;
  displayNum?: number;
  heading?: string;
  hideSeparator?: boolean;
  href?: string;
  nativoCardId?: number;
  /** Fallback article when Nativo does not fill; must be set when nativoCardId is set. */
  nativoFallbackArticle?: ArticlePreviewGrid;
  priority?: boolean;
  dataCy?: string;
}

export const ArticleSectionCarousel = ({
  articles,
  className,
  dataCy,
  displayNum = 4,
  heading,
  hideSeparator = false,
  href,
  nativoCardId,
  nativoFallbackArticle,
  priority,
}: ArticleSectionCarouselProps) => {
  const [nativoSlotFilled, setNativoSlotFilled] = useState(false);

  const onNativoCarouselFilled = useCallback(() => {
    setNativoSlotFilled(true);
  }, []);

  const articleIdsKey = articles.map((a) => a.databaseId).join(',');

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally reset when carousel package or Nativo slot identity changes
  useEffect(() => {
    setNativoSlotFilled(false);
  }, [articleIdsKey, nativoFallbackArticle?.databaseId, nativoCardId]);

  if (articles.length === 0) {
    return null;
  }

  return (
    <div
      className={cn('flex flex-col gap-6', className)}
      data-cy={dataCy}
    >
      {heading && <StandardHeading href={href}>{heading}</StandardHeading>}
      <Carousel
        displayNum={displayNum}
        hideSeparator={hideSeparator}
      >
        {articles.map((article, i) => {
          const elements = [
            <ArticleCardGrid
              article={article}
              className='snap-start min-w-0 pl-4 md:pl-0'
              index={i + 1}
              key={article.databaseId}
              priority={priority}
              source='carousel'
            />,
          ];

          if (nativoCardId && nativoFallbackArticle && i === displayNum - 3) {
            elements.push(
              <NativoCarouselFeedSlot
                fallbackArticle={nativoFallbackArticle}
                key={`nativo-topic-${nativoCardId}`}
                nativoCardId={nativoCardId}
                onNativoFilledChange={onNativoCarouselFilled}
                priority={priority}
              />,
            );
          }

          return elements;
        })}
        {nativoCardId && nativoFallbackArticle && nativoSlotFilled && (
          <ArticleCardGrid
            article={nativoFallbackArticle}
            className='snap-start min-w-0 pl-4 md:pl-0'
            index={articles.length + 1}
            key={`nativo-shift-${nativoFallbackArticle.databaseId}`}
            priority={priority}
            source='carousel'
          />
        )}
      </Carousel>
    </div>
  );
};
